import React, { useState } from 'react';
import Modal from 'src/shared/components/modal/Modal';
import { PrimaryButton } from 'src/shared/components/button/Button';
import Input from 'src/shared/components/input/Input';
import useBoolean from 'src/shared/hooks/useBoolean';
import { Pencil as EditOutlinedIcon, FileText as InsertDriveFileOutlinedIcon } from 'lucide-react';
import { FileRow } from './FilesTable';

type RenameFileModalProps = {
    file: FileRow;
    onClose: () => void;
    onSave: (newName: string) => Promise<void>;
};

const RenameFileModal = ({ file, onClose, onSave }: RenameFileModalProps) => {
    const getFileNameWithoutExtension = (filename: string) => {
        const lastDotIndex = filename.lastIndexOf('.');
        return lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;
    };

    const getFileExtension = (filename: string) => {
        const lastDotIndex = filename.lastIndexOf('.');
        return lastDotIndex > 0 ? filename.substring(lastDotIndex) : '';
    };

    const [newName, setNewName] = useState(getFileNameWithoutExtension(file.name));
    const [isSaving, saving] = useBoolean(false);
    const fileExtension = getFileExtension(file.name);

    const handleSave = async () => {
        if (newName.trim()) {
            saving.on();
            try {
                await onSave(newName.trim() + fileExtension);
            } finally {
                saving.off();
            }
        }
    };

    return (
        <Modal onClose={onClose} dismissOnClickOutside={false}>
            <div className='w-[700px] max-w-[90vw]'>
                {/* Header */}
                <div className='flex items-center gap-4 mb-8'>
                    <div className='w-14 h-14 rounded-full border-2 border-teal-60 flex items-center justify-center'>
                        <EditOutlinedIcon className='text-teal-60' sx={{ fontSize: 28 }} />
                    </div>
                    <h2 className='text-[28px] font-normal text-teal-60 m-0'>Rename file</h2>
                </div>

                {/* File Info */}
                <div className='mb-8 p-4 bg-grey-0 rounded-md flex items-center gap-4'>
                    <InsertDriveFileOutlinedIcon className='text-grey-80' sx={{ fontSize: 48 }} />
                    <div className='flex-1'>
                        <p className='text-base font-medium text-navy mb-1'>{file.name}</p>
                        <p className='text-sm text-grey-60 mb-0'>{file.sizeLabel}</p>
                    </div>
                </div>

                {/* File name input */}
                <div className='mb-8'>
                    <label className='block text-teal-60 text-sm font-medium mb-2'>File name</label>
                    <div className='flex items-center gap-2'>
                        <Input value={newName} onChange={e => setNewName(e.target.value)} className='flex-1' autoFocus />
                        <span className='text-grey-80 font-medium'>{fileExtension}</span>
                    </div>
                </div>

                {/* Actions */}
                <div className='flex justify-end'>
                    <PrimaryButton
                        onClick={handleSave}
                        disabled={!newName.trim() || isSaving}
                        className='min-w-[140px] px-8 py-3 text-base disabled:opacity-40 disabled:cursor-not-allowed'
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </PrimaryButton>
                </div>
            </div>
        </Modal>
    );
};

export default RenameFileModal;
